import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getLeaderboards } from "@/api/leaderboards";
import type { LeaderboardEntry } from "@/api/leaderboards";
import { useAuth } from "@/auth/AuthProvider";
import type { WidgetComponentProps } from "../types";

const LB_MISSION = "Racing - Basics";

function formatLap(seconds: number | null): string {
  if (seconds == null) return "—";
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return `${minutes}:${rem.toFixed(2).padStart(5, "0")}`;
}

function medalClass(rank: number): string {
  if (rank === 1) return "leaderboard-row--gold";
  if (rank === 2) return "leaderboard-row--silver";
  if (rank === 3) return "leaderboard-row--bronze";
  return "";
}

function LeaderboardRow({
  entry,
  isMe,
  compact,
}: {
  entry: LeaderboardEntry;
  isMe: boolean;
  compact?: boolean;
}) {
  return (
    <li
      className={`leaderboard-row ${medalClass(entry.rank)} ${
        isMe ? "leaderboard-row--me" : ""
      }`}
    >
      <span className="leaderboard-row__rank">#{entry.rank}</span>
      <span className="leaderboard-row__name">
        {entry.username}
        {isMe && <span className="leaderboard-row__me-pill">You</span>}
      </span>
      {!compact && (
        <span className="leaderboard-row__meta">
          {entry.flight_count} flight{entry.flight_count === 1 ? "" : "s"}
        </span>
      )}
      <span className="leaderboard-row__value">
        {formatLap(entry.best_lap_seconds)}
      </span>
    </li>
  );
}

export function LeaderboardWidget({ sizeVariant }: WidgetComponentProps) {
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["leaderboards", LB_MISSION],
    queryFn: () => getLeaderboards(LB_MISSION),
    staleTime: 60_000,
  });

  const rows = data?.rows ?? [];
  const myEntry = useMemo(
    () => rows.find((r) => user && r.user_id === user.id) ?? null,
    [rows, user],
  );

  if (isLoading) return <p className="widget-empty">Loading…</p>;
  if (isError) return <p className="widget-empty">Failed to load.</p>;
  if (!rows.length) {
    return <p className="widget-empty">No leaderboard entries yet.</p>;
  }

  const limit = sizeVariant === "s" ? 5 : sizeVariant === "m" ? 10 : 20;
  const topRows = rows.slice(0, limit);
  const meInTop = myEntry ? topRows.some((r) => r.user_id === myEntry.user_id) : false;

  if (sizeVariant === "s") {
    return (
      <div className="leaderboard leaderboard--s">
        <p className="eyebrow">
          {LB_MISSION} · top {limit}
        </p>
        <ol className="leaderboard__list">
          {topRows.map((entry) => (
            <LeaderboardRow
              key={entry.user_id}
              entry={entry}
              isMe={!!user && entry.user_id === user.id}
              compact
            />
          ))}
          {!meInTop && myEntry && (
            <>
              <li className="leaderboard-row__divider">…</li>
              <LeaderboardRow entry={myEntry} isMe compact />
            </>
          )}
        </ol>
      </div>
    );
  }

  if (sizeVariant === "l") {
    return (
      <div className="leaderboard leaderboard--l">
        <div className="leaderboard__head">
          <div>
            <p className="eyebrow">Leaderboard</p>
            <h3 className="leaderboard__title">{LB_MISSION}</h3>
          </div>
        </div>
        <div className="leaderboard__columns">
          <span>Rank</span>
          <span>Pilot</span>
          <span>Flights</span>
          <span>Best lap</span>
        </div>
        <ol className="leaderboard__list leaderboard__list--dense">
          {topRows.map((entry) => (
            <LeaderboardRow
              key={entry.user_id}
              entry={entry}
              isMe={!!user && entry.user_id === user.id}
            />
          ))}
          {!meInTop && myEntry && (
            <>
              <li className="leaderboard-row__divider">
                …{rows.length - topRows.length} more
              </li>
              <LeaderboardRow entry={myEntry} isMe />
            </>
          )}
        </ol>
        <Link to="/leaderboards" className="text-link">
          Full leaderboard →
        </Link>
      </div>
    );
  }

  return (
    <div className="leaderboard leaderboard--m">
      <div className="leaderboard__head">
        <p className="eyebrow">
          {LB_MISSION} · top {limit}
        </p>
      </div>
      <ol className="leaderboard__list">
        {topRows.map((entry) => (
          <LeaderboardRow
            key={entry.user_id}
            entry={entry}
            isMe={!!user && entry.user_id === user.id}
          />
        ))}
        {!meInTop && myEntry && (
          <>
            <li className="leaderboard-row__divider">
              …{rows.length - topRows.length} more
            </li>
            <LeaderboardRow entry={myEntry} isMe />
          </>
        )}
      </ol>
      <Link to="/leaderboards" className="text-link">
        Full leaderboard →
      </Link>
    </div>
  );
}
